import axios from "axios";
import { emitUpgrade } from "./upgradeModalBus";
import { hasBlocked, isBlockedId } from "./blockedStore";

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

// Attach Authorization header (JWT) on every request
api.interceptors.request.use((config) => {
    try {
        const token = localStorage.getItem("saatSaheliToken");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
    } catch { /* ignore */ }
    return config;
});

// Surface plan-limit hits globally. The backend returns HTTP 403 with
// { error, upgradeRequired: true } when an action exceeds the user's plan.
// We pop the global upgrade modal and tag the error as `isUpgradeRequired` so
// call sites can skip their own (now redundant) inline error message. The
// rejection still propagates so callers can early-return.
// Hide items posted by people the user has blocked from public list responses (comments,
// books, galleries, articles, recipes, podcasts…). Admin, account and block-list calls are
// left untouched so moderation and the user's own settings always see everything.
const UNFILTERED = ["/api/admin", "/api/blocks", "/api/account", "/api/auth", "/api/reports", "/api/payments", "/api/messages"];
const notBlocked = (item) => !(item && typeof item === "object" && isBlockedId(item.userId));
function dropBlocked(data) {
    if (Array.isArray(data)) return data.filter(notBlocked);
    if (data && typeof data === "object" && !(data instanceof Blob)) {
        const out = { ...data };
        Object.keys(out).forEach((k) => { if (Array.isArray(out[k])) out[k] = out[k].filter(notBlocked); });
        return out;
    }
    return data;
}

api.interceptors.response.use(
    (response) => {
        try {
            const url = response.config?.url || "";
            if (hasBlocked() && (response.config?.method || "get") === "get" && !UNFILTERED.some((p) => url.includes(p))) {
                response.data = dropBlocked(response.data);
            }
        } catch { /* never break a response over filtering */ }
        return response;
    },
    async (error) => {
        const resp = error?.response;
        // Our token was rejected (expired, account closed, secret rotated): sign out so the
        // page stops showing a signed-in view. Wrong-password 401s carry other messages
        // and are deliberately not treated as a lost session.
        try {
            const sentToken = !!error?.config?.headers?.Authorization;
            if (resp && resp.status === 401 && sentToken && resp.data && resp.data.error === "Authentication required") {
                window.dispatchEvent(new Event("ss:auth-rejected"));
            }
        } catch { /* ignore */ }
        if (resp && resp.status === 403) {
            let body = resp.data;
            if (body instanceof Blob) {
                // Export requests use responseType:'blob', so the error body is a
                // Blob even when it's actually JSON. Reading it does not consume
                // the Blob, so export call sites can still parse resp.data again.
                try { body = JSON.parse(await body.text()); } catch { body = null; }
            }
            if (body && body.upgradeRequired) {
                error.isUpgradeRequired = true;
                emitUpgrade(body.error);
            }
        }
        return Promise.reject(error);
    }
);

/** True when an error was a plan-limit 403 already handled by the global modal. */
export function isUpgradeRequiredError(error) {
    return !!(error && error.isUpgradeRequired);
}

export function getAnonId() {
    let id = localStorage.getItem("saatSaheliAnonId");
    if (!id) {
        id = "anon-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("saatSaheliAnonId", id);
    }
    return id;
}

// Public profile link. Creators have a unique handle ("url id") so the canonical
// link is /profile/Chitra-Sharma. Callers that only know the user id (older API
// payloads without authorHandle) fall back to the legacy /profile/{id}/{name}
// form, which PublicProfile resolves and then redirects to the handle URL.
export function profileUrl(userId, name, handle) {
    if (handle) return `/profile/${encodeURIComponent(handle)}`;
    const slug = (name || "")
        .trim()
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "-");
    return slug ? `/profile/${userId}/${encodeURIComponent(slug)}` : `/profile/${userId}`;
}

export default api;
